/**
 * «Контакты» (изменение v1.2) — по образцу вкладки «Контакты» в Telegram.
 *
 * Контакт — человек, с которым можно переписываться: совпадение (ПС §10) или
 * прямой диалог по никнейму (BS §7.10). Экран — алфавитный список со статусами
 * присутствия и индексом букв у правого края; «Пригласить друзей» стоит под
 * списком, как в Telegram. Нажатие открывает диалог, долгое нажатие — меню
 * (M-05). Просмотр списка снимает отметку «новое» (ПС §10).
 *
 * Своего поля поиска здесь нет: поиск — круглая кнопка у панели вкладок, и он
 * умеет больше (никнеймы вне контактов). Лента новых совпадений живёт в
 * «Чатах», где с ними и начинают переписку; здесь «новое» помечено точкой.
 */
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { contactDisplayName, useContacts, type Contact } from '@entities/contact/model'
import { useContactActions } from '@features/contacts/use-contacts'
import type { Match } from '@shared/api/types'
import { uuid } from '@shared/api/client'
import { UiKey, queueRepo, uiStateRepo, useReplica } from '@shared/db'
import { haptic } from '@shared/lib/motion'
import { triggerQueue } from '@shared/sync/queue-runner'
import {
  ActionItem,
  ActionList,
  Avatar,
  Badge,
  Button,
  EmptyState,
  Icon,
  Sheet,
  cx,
  useToast,
} from '@shared/ui'

import { KEY_ITEM_ATTR, KEY_LIST_ATTR } from '../../model/hotkeys'
import { Screen } from '../../layouts/Screen'
import { formatListTime, formatPresence } from '../../lib/format'
import { useLivePresence } from '../../model/presence-store'
import styles from './contacts.module.css'

type SortMode = 'name' | 'activity'

/** Долгое нажатие на строке, после которого открывается меню, а не диалог. */
const LONG_PRESS_MS = 450

const collator = new Intl.Collator('ru', { sensitivity: 'base' })

/** Первая буква имени для группы; всё, что не буква, — под «#». */
function groupLetter(contact: Contact): string {
  if (contact.availability !== 'available') return '#'
  const first = (contact.name ?? '').trim().charAt(0).toUpperCase()
  return /\p{L}/u.test(first) ? first : '#'
}

/** Ранг присутствия для сортировки «по активности»: в сети → недавно → … → неизвестно. */
function activityRank(contact: Contact, presence: Contact['presence']): number {
  if (contact.availability !== 'available') return 9
  if (!presence) return 5
  if (presence.online) return 0
  switch (presence.last_seen_bucket) {
    case 'recently':
      return 1
    case 'today':
      return 2
    case 'this_week':
      return 3
    case 'long_ago':
      return 4
    default:
      return 5
  }
}

export function ContactsScreen(): React.ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const { items: contacts, loaded } = useContacts()
  const { remove: removeContact } = useContactActions()
  // Порядок списка переживает перезапуск: как выбор «Sort» в Telegram
  const storedSort = useReplica(() => uiStateRepo.get<SortMode>(UiKey.contactsSort), [], null)
  const [sortOverride, setSortOverride] = useState<SortMode | null>(null)
  const sort: SortMode = sortOverride ?? storedSort ?? 'name'
  const [sortOpen, setSortOpen] = useState(false)
  const chooseSort = (next: SortMode): void => {
    setSortOverride(next)
    setSortOpen(false)
    void uiStateRepo.set(UiKey.contactsSort, next)
  }

  const [menuFor, setMenuFor] = useState<{ contact: Contact; anchor: DOMRect } | null>(null)
  const openMenu = useCallback((contact: Contact, anchor: DOMRect) => setMenuFor({ contact, anchor }), [])

  const unseen = useMemo(
    () => contacts.filter((c) => c.match && !c.match.seen).map((c) => c.match as Match),
    [contacts],
  )

  /*
   * Просмотр списка снимает отметку «новое» (ПС §10). Операция идемпотентна и
   * ставится в очередь: оптимистично считаем просмотренным.
   */
  useEffect(() => {
    if (!unseen.length) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const cancel = (): void => {
      if (timer !== null) clearTimeout(timer)
      timer = null
    }
    const schedule = (): void => {
      cancel()
      if (document.visibilityState !== 'visible') return
      timer = setTimeout(() => {
        timer = null
        if (document.visibilityState === 'visible') void markSeen(unseen).catch(() => undefined)
      }, 1200)
    }
    schedule()
    document.addEventListener('visibilitychange', schedule)
    return () => {
      cancel()
      document.removeEventListener('visibilitychange', schedule)
    }
  }, [unseen])

  const openContact = useCallback(
    (contact: Contact) => {
      // Как в Telegram: нажатие на контакт открывает диалог; без диалога — профиль
      if (contact.conversationId) {
        void navigate({ to: '/chats/$conversationId', params: { conversationId: contact.conversationId } })
        return
      }
      void navigate({ to: '/users/$userId', params: { userId: contact.id } })
    },
    [navigate],
  )

  const invite = async (): Promise<void> => {
    const url = `${location.origin}/`
    const text = 'Я в «Движе» — заходи, найдём чем заняться вместе'
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: 'Движ', text, url })
        return
      }
    } catch (error) {
      // Отмена системного диалога — не ошибка
      if (error instanceof DOMException && error.name === 'AbortError') return
    }
    try {
      await navigator.clipboard.writeText(url)
      toast.show('Ссылка скопирована')
    } catch {
      toast.show(url)
    }
  }

  const contentRef = useRef<HTMLDivElement>(null)
  const menuContact = menuFor?.contact ?? null

  return (
    <>
      <div className={styles['layout']}>
        <Screen
          title="Контакты"
          flush
          contentRef={contentRef}
          className={styles['screen']}
          contentClassName={styles['content']}
          actions={
            <>
              <Button
                icon="sort"
                variant="ghost"
                aria-label="Порядок списка"
                aria-haspopup="menu"
                onClick={() => setSortOpen(true)}
              />
              <Button
                icon="user-plus"
                variant="ghost"
                aria-label="Добавить контакт"
                onClick={() => void navigate({ to: '/contacts/add' })}
              />
            </>
          }
        >
          <div className={styles['narrow']}>
            {!loaded ? null : !contacts.length ? (
              <EmptyState
                icon="contacts"
                title="Контактов пока нет"
                text="Добавьте человека по номеру телефона или найдите по никнейму. Взаимное «Го» в «Движе» добавляет в контакты само."
                actions={
                  <div className={styles['emptyActions']}>
                    <Button variant="primary" onClick={() => void navigate({ to: '/contacts/add' })}>
                      Добавить контакт
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => void navigate({ to: '/search', search: { scope: 'contacts' } })}
                    >
                      Найти по никнейму
                    </Button>
                  </div>
                }
              />
            ) : sort === 'name' ? (
              <GroupedList
                contacts={contacts}
                onOpen={openContact}
                onMenu={openMenu}
                contentRef={contentRef}
              />
            ) : (
              <FlatList contacts={contacts} sort={sort} onOpen={openContact} onMenu={openMenu} />
            )}

            {/* «Пригласить друзей» — под списком, как в Telegram */}
            {loaded && contacts.length ? (
              <>
                <button
                  type="button"
                  className={styles['inviteRow']}
                  onClick={() => void navigate({ to: '/contacts/add' })}
                >
                  <span className={styles['inviteIcon']}>
                    <Icon name="user-plus" size={22} />
                  </span>
                  <span className={styles['inviteLabel']}>Добавить контакт</span>
                </button>
                <button type="button" className={styles['inviteRow']} onClick={() => void invite()}>
                  <span className={styles['inviteIcon']}>
                    <Icon name="share" size={22} />
                  </span>
                  <span className={styles['inviteLabel']}>Пригласить друзей</span>
                </button>
              </>
            ) : null}
          </div>
        </Screen>
      </div>

      {/* Порядок списка — как «Sort» в Telegram */}
      <Sheet open={sortOpen} onClose={() => setSortOpen(false)} title="Порядок списка" flush>
        <ActionList>
          <ActionItem
            icon="at"
            label="По имени"
            hint={sort === 'name' ? 'Выбрано' : undefined}
            onClick={() => chooseSort('name')}
          />
          <ActionItem
            icon="clock"
            label="По активности"
            hint={sort === 'activity' ? 'Выбрано' : undefined}
            onClick={() => chooseSort('activity')}
          />
        </ActionList>
      </Sheet>

      {/* M-05: меню контакта */}
      <Sheet
        open={menuFor !== null}
        onClose={() => setMenuFor(null)}
        anchorRect={menuFor?.anchor ?? null}
        title={menuContact ? contactDisplayName(menuContact) : 'Контакт'}
        flush
      >
        <ActionList>
          <ActionItem
            icon="send"
            label="Написать"
            disabled={menuContact?.availability !== 'available'}
            onClick={() => {
              const target = menuContact
              setMenuFor(null)
              // Изменение v1.6: контакту можно написать и до первого диалога — он откроется сам
              if (target) openContact(target)
            }}
          />
          <ActionItem
            icon="user"
            label="Открыть профиль"
            disabled={menuContact?.availability !== 'available'}
            onClick={() => {
              const target = menuContact
              setMenuFor(null)
              if (target) void navigate({ to: '/users/$userId', params: { userId: target.id } })
            }}
          />
          <ActionItem
            icon="flag"
            label="Пожаловаться"
            disabled={menuContact?.availability !== 'available'}
            onClick={() => {
              const target = menuContact
              setMenuFor(null)
              if (target) {
                void navigate({
                  to: '/users/$userId',
                  params: { userId: target.id },
                  search: { action: 'report' },
                })
              }
            }}
          />
          <ActionItem
            icon="trash"
            label="Удалить из контактов"
            onClick={() => {
              const target = menuContact
              setMenuFor(null)
              if (target) void removeContact(target.id)
            }}
          />
          <ActionItem
            icon="block"
            label="Заблокировать"
            danger
            disabled={menuContact?.availability !== 'available'}
            onClick={() => {
              const target = menuContact
              setMenuFor(null)
              if (target) {
                void navigate({
                  to: '/users/$userId',
                  params: { userId: target.id },
                  search: { action: 'block' },
                })
              }
            }}
          />
        </ActionList>
      </Sheet>
    </>
  )
}

async function markSeen(matches: readonly Match[]): Promise<void> {
  for (const match of matches) {
    await queueRepo.enqueue({
      op_id: uuid(),
      kind: 'match_seen',
      match_id: match.id,
      created_at: new Date().toISOString(),
      attempt_count: 0,
      next_attempt_at: null,
      last_error: null,
      claimed_by: null,
      claimed_at: null,
      idempotency_key: null,
    })
  }
  triggerQueue()
}

/* ===================================================================== */
/* Алфавитный список с индексом                                           */
/* ===================================================================== */

function GroupedList({
  contacts,
  onOpen,
  onMenu,
  contentRef,
}: {
  contacts: Contact[]
  onOpen: (contact: Contact) => void
  onMenu: (contact: Contact, anchor: DOMRect) => void
  contentRef: React.RefObject<HTMLDivElement>
}) {
  const groups = useMemo(() => {
    const map = new Map<string, Contact[]>()
    for (const contact of contacts) {
      const letter = groupLetter(contact)
      const list = map.get(letter) ?? []
      list.push(contact)
      map.set(letter, list)
    }
    const letters = [...map.keys()].sort((a, b) => {
      if (a === '#') return 1
      if (b === '#') return -1
      return collator.compare(a, b)
    })
    return letters.map((letter) => ({
      letter,
      items: (map.get(letter) ?? []).sort((a, b) =>
        collator.compare(contactDisplayName(a), contactDisplayName(b)),
      ),
    }))
  }, [contacts])

  const groupRefs = useRef(new Map<string, HTMLElement>())

  const jumpTo = useCallback(
    (letter: string) => {
      const content = contentRef.current
      const target = groupRefs.current.get(letter)
      if (!content || !target) return
      const top = target.getBoundingClientRect().top - content.getBoundingClientRect().top + content.scrollTop
      content.scrollTo({ top, behavior: 'auto' })
    },
    [contentRef],
  )

  return (
    <>
      <div className={styles['list']} {...{ [KEY_LIST_ATTR]: true }}>
        {groups.map((group) => (
          <section
            key={group.letter}
            className={styles['group']}
            ref={(element) => {
              if (element) groupRefs.current.set(group.letter, element)
              else groupRefs.current.delete(group.letter)
            }}
            aria-label={group.letter}
          >
            <div className={styles['groupLetter']} aria-hidden>
              {group.letter}
            </div>
            {group.items.map((contact, index) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                divider={index > 0}
                onOpen={onOpen}
                onMenu={onMenu}
              />
            ))}
          </section>
        ))}
      </div>

      {/* Указатель нужен, когда по нему действительно прыгают: на двух буквах это просто шум */}
      {groups.length > 3 ? <LetterIndex letters={groups.map((g) => g.letter)} onJump={jumpTo} /> : null}
    </>
  )
}

/** Индекс букв у правого края: нажатие или ведение пальцем прокручивает к группе, как в iOS */
function LetterIndex({ letters, onJump }: { letters: string[]; onJump: (letter: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const lastRef = useRef<string | null>(null)

  const pick = (clientY: number): void => {
    const element = ref.current
    if (!element) return
    const rect = element.getBoundingClientRect()
    const index = Math.min(
      letters.length - 1,
      Math.max(0, Math.floor(((clientY - rect.top) / rect.height) * letters.length)),
    )
    const letter = letters[index]
    if (!letter || letter === lastRef.current) return
    lastRef.current = letter
    haptic('light')
    onJump(letter)
  }

  return (
    <div
      ref={ref}
      className={styles['index']}
      role="navigation"
      aria-label="Алфавитный указатель"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId)
        lastRef.current = null
        pick(event.clientY)
      }}
      onPointerMove={(event) => {
        if (event.buttons === 0) return
        pick(event.clientY)
      }}
      onPointerUp={() => {
        lastRef.current = null
      }}
    >
      {letters.map((letter) => (
        <button
          key={letter}
          type="button"
          className={styles['indexLetter']}
          tabIndex={-1}
          aria-label={`К букве ${letter}`}
        >
          {letter}
        </button>
      ))}
    </div>
  )
}

/* ===================================================================== */
/* Плоский список (поиск и порядок «по активности»)                       */
/* ===================================================================== */

function FlatList({
  contacts,
  sort,
  onOpen,
  onMenu,
}: {
  contacts: Contact[]
  sort: SortMode
  onOpen: (contact: Contact) => void
  onMenu: (contact: Contact, anchor: DOMRect) => void
}) {
  const sorted = useMemo(() => {
    const items = [...contacts]
    items.sort((a, b) => {
      if (sort === 'activity') {
        const rank = activityRank(a, a.presence) - activityRank(b, b.presence)
        if (rank !== 0) return rank
      }
      return collator.compare(contactDisplayName(a), contactDisplayName(b))
    })
    return items
  }, [contacts, sort])

  return (
    <div className={cx(styles['list'], styles['flat'])} {...{ [KEY_LIST_ATTR]: true }}>
      {sorted.map((contact, index) => (
        <ContactRow key={contact.id} contact={contact} divider={index > 0} onOpen={onOpen} onMenu={onMenu} />
      ))}
    </div>
  )
}

/* ===================================================================== */
/* Строка контакта                                                        */
/* ===================================================================== */

function ContactRow({
  contact,
  divider,
  onOpen,
  onMenu,
}: {
  contact: Contact
  divider: boolean
  onOpen: (contact: Contact) => void
  onMenu: (contact: Contact, anchor: DOMRect) => void
}) {
  const presence = useLivePresence(contact.id, contact.presence)
  const presenceText = contact.availability === 'available' ? formatPresence(presence) : null
  const isNew = Boolean(contact.match && !contact.match.seen)

  /*
   * Долгое нажатие открывает меню, а не диалог (как в списке чатов): таймер
   * стартует по касанию, сбрасывается движением или отпусканием.
   */
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pressStart = useRef<{ x: number; y: number } | null>(null)
  const longPressed = useRef(false)

  const cancelPress = (): void => {
    if (pressTimer.current) clearTimeout(pressTimer.current)
    pressTimer.current = null
    pressStart.current = null
  }

  const status = presenceText
    ? presenceText
    : contact.direct && !contact.match
      ? contact.username
        ? `@${contact.username}`
        : 'Диалог по никнейму'
      : contact.match
        ? `Совпали ${formatListTime(contact.match.created_at)}`
        : ''

  return (
    <button
      type="button"
      className={cx(styles['row'], divider && styles['rowDivider'])}
      {...{ [KEY_ITEM_ATTR]: true }}
      aria-label={`${contactDisplayName(contact)}${status ? `, ${status}` : ''}`}
      onClick={() => {
        if (longPressed.current) {
          longPressed.current = false
          return
        }
        onOpen(contact)
      }}
      onContextMenu={(event) => {
        event.preventDefault()
        if (longPressed.current) return
        onMenu(contact, event.currentTarget.getBoundingClientRect())
      }}
      onPointerDown={(event) => {
        if (event.pointerType !== 'touch') return
        longPressed.current = false
        pressStart.current = { x: event.clientX, y: event.clientY }
        const target = event.currentTarget
        pressTimer.current = setTimeout(() => {
          pressTimer.current = null
          longPressed.current = true
          haptic('medium')
          onMenu(contact, target.getBoundingClientRect())
        }, LONG_PRESS_MS)
      }}
      onPointerMove={(event) => {
        const start = pressStart.current
        if (!start || !pressTimer.current) return
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) cancelPress()
      }}
      onPointerUp={cancelPress}
      onPointerCancel={cancelPress}
      onPointerLeave={cancelPress}
    >
      <Avatar
        name={contact.name}
        photo={contact.photo}
        availability={contact.availability}
        size="lg"
        online={presence?.online ?? false}
      />

      <span className={styles['rowBody']}>
        <span className={styles['rowName']}>{contactDisplayName(contact)}</span>
        {status ? (
          <span className={cx(styles['rowStatus'], presence?.online && styles['rowStatusOnline'])}>
            {status}
          </span>
        ) : null}
      </span>

      {isNew ? (
        <span className={styles['rowNew']}>
          <Badge dot label="Новое совпадение" />
        </span>
      ) : null}
    </button>
  )
}
